import React, { useEffect, useState } from 'react';
import {
    CalendarClock, Plus, Trash2, Users, CheckCircle2, AlertCircle,
} from 'lucide-react';
import patientCallingService from '../../../../services/patientCalling.service';
import styles from './ScheduleCampaignFlow.module.css';

export type ScheduleType = 'one_time' | 'recurring';

export interface ExtraParam { key: string; value: string }

export interface ScheduleFormState {
    calling_system_call_type: string;
    calling_system_entity_type: string;
    schedule_type: ScheduleType;
    scheduled_at: string;
    cron_expression: string;
    extra_parameters: ExtraParam[];
}

interface Props {
    callType: 'payment_reminder' | 'patient_info_gathering';
    selectedCount: number;
    formState: ScheduleFormState;
    onChange: (state: ScheduleFormState) => void;
    errors: Partial<Record<keyof ScheduleFormState, string>>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CALL_TYPE_LABELS: Record<string, string> = {
    payment_reminder: 'Payment Reminder',
    patient_info_gathering: 'Patient Info Gathering',
};

const ENTITY_TYPE_OPTIONS = ['Patient', 'Facility', 'Provider'];

const CRON_PRESETS: { label: string; value: string; hint: string }[] = [
    { label: 'Daily 9am',    value: '0 9 * * *',   hint: 'Every day at 9:00 AM' },
    { label: 'Mon 9am',      value: '0 9 * * 1',   hint: 'Every Monday at 9:00 AM' },
    { label: 'Bi-weekly',    value: '0 9 1,15 * *', hint: '1st & 15th of every month' },
    { label: 'Weekdays 8am', value: '0 8 * * 1-5', hint: 'Mon–Fri at 8:00 AM' },
];

// ─── Component ────────────────────────────────────────────────────────────────

const Step3ScheduleConfig: React.FC<Props> = ({
    callType, selectedCount, formState, onChange, errors,
}) => {
    const [config, setConfig] = useState<any>(null);
    const [configLoading, setConfigLoading] = useState(true);

    useEffect(() => {
        patientCallingService.getConfig()
            .then(setConfig)
            .catch(() => setConfig(null))
            .finally(() => setConfigLoading(false));
    }, []);

    const set = <K extends keyof ScheduleFormState>(key: K, val: ScheduleFormState[K]) =>
        onChange({ ...formState, [key]: val });

    const addParam = () =>
        set('extra_parameters', [...formState.extra_parameters, { key: '', value: '' }]);

    const removeParam = (i: number) =>
        set('extra_parameters', formState.extra_parameters.filter((_, idx) => idx !== i));

    const updateParam = (i: number, field: keyof ExtraParam, val: string) => {
        const next = [...formState.extra_parameters];
        next[i] = { ...next[i], [field]: val };
        set('extra_parameters', next);
    };

    return (
        <div className={styles.stepContent}>
            <div className={styles.stepHeading}>
                <CalendarClock size={22} className={styles.stepHeadingIcon} />
                <div>
                    <h3 className={styles.stepTitle}>Schedule Configuration</h3>
                    <p className={styles.stepSubtitle}>
                        Configure the schedule for your campaign. Provider and system settings are managed by your organisation config.
                    </p>
                </div>
            </div>

            {/* Summary banner */}
            <div className={styles.summaryBanner}>
                <div className={styles.summaryItem}>
                    <Users size={15} />
                    <span><strong>{selectedCount}</strong> patient{selectedCount !== 1 ? 's' : ''} selected</span>
                </div>
                <div className={styles.summaryDivider} />
                <div className={styles.summaryItem}>
                    <CheckCircle2 size={15} />
                    <span>Call type: <strong>{CALL_TYPE_LABELS[callType]}</strong></span>
                </div>
                {!configLoading && config && (
                    <>
                        <div className={styles.summaryDivider} />
                        <div className={styles.summaryItem}>
                            <span className={styles.configPill}>{config.llm_provider || 'LLM'}</span>
                            <span className={styles.configPill}>{config.tts_provider || 'TTS'}</span>
                        </div>
                    </>
                )}
            </div>

            {!configLoading && !config && (
                <div className={styles.configWarning}>
                    <AlertCircle size={15} />
                    <span>No organisation config found. Webhook URL, API key, LLM and TTS providers must be configured before scheduling.</span>
                </div>
            )}

            {/* ── Section: Calling System ─────────────────────────────────── */}
            <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>Calling System</div>
                <div className={styles.formGrid2}>
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Call Type
                        </label>
                        <div className={`${styles.readonlyField}`}>
                            <span className={`${styles.callTypeBadge} ${callType === 'payment_reminder' ? styles.badgeGreen : styles.badgePurple}`}>
                                {CALL_TYPE_LABELS[callType]}
                            </span>
                            <span className={styles.readonlyNote}>Set in Step 1</span>
                        </div>
                        <input type="hidden" value={formState.calling_system_call_type} readOnly />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Entity Type <span className={styles.required}>*</span>
                        </label>
                        <select
                            className={`${styles.select} ${errors.calling_system_entity_type ? styles.inputError : ''}`}
                            value={formState.calling_system_entity_type}
                            onChange={e => set('calling_system_entity_type', e.target.value)}
                        >
                            <option value="">Select entity type…</option>
                            {ENTITY_TYPE_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                        {errors.calling_system_entity_type && (
                            <span className={styles.fieldError}>{errors.calling_system_entity_type}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Section: Schedule ──────────────────────────────────────── */}
            <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>Schedule</div>

                <div className={styles.scheduleToggle}>
                    <button
                        type="button"
                        className={`${styles.scheduleToggleBtn} ${formState.schedule_type === 'one_time' ? styles.scheduleToggleBtnActive : ''}`}
                        onClick={() => set('schedule_type', 'one_time')}
                    >
                        One-Time
                    </button>
                    {/* <button
                        type="button"
                        className={`${styles.scheduleToggleBtn} ${formState.schedule_type === 'recurring' ? styles.scheduleToggleBtnActive : ''}`}
                        onClick={() => set('schedule_type', 'recurring')}
                    >
                        Recurring
                    </button> */}
                </div>

                {formState.schedule_type === 'one_time' ? (
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Date & Time <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="datetime-local"
                            className={`${styles.input} ${errors.scheduled_at ? styles.inputError : ''}`}
                            value={formState.scheduled_at}
                            onChange={e => set('scheduled_at', e.target.value)}
                        />
                        {errors.scheduled_at && (
                            <span className={styles.fieldError}>{errors.scheduled_at}</span>
                        )}
                    </div>
                ) : (
                    <div className={styles.formGroup}>
                        <label className={styles.label}>
                            Cron Expression <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.presetRow}>
                            {CRON_PRESETS.map(p => (
                                <button
                                    key={p.value}
                                    type="button"
                                    title={p.hint}
                                    className={`${styles.presetChip} ${formState.cron_expression === p.value ? styles.presetChipActive : ''}`}
                                    onClick={() => set('cron_expression', p.value)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            className={`${styles.input} ${errors.cron_expression ? styles.inputError : ''}`}
                            placeholder="Custom cron: 0 9 * * *"
                            value={formState.cron_expression}
                            onChange={e => set('cron_expression', e.target.value)}
                        />
                        {errors.cron_expression && (
                            <span className={styles.fieldError}>{errors.cron_expression}</span>
                        )}
                        <span className={styles.hintText}>
                            Format: minute hour day month weekday. Example: <code>0 9 * * 1</code> = every Monday at 9am
                        </span>
                    </div>
                )}
            </div>

            {/* ── Section: Extra Parameters ──────────────────────────────── */}
            <div className={styles.formSection}>
                <div className={styles.formSectionTitleRow}>
                    <span className={styles.formSectionTitle}>Extra Parameters</span>
                    <button type="button" className={styles.addParamBtn} onClick={addParam}>
                        <Plus size={13} /> Add
                    </button>
                </div>
                <p className={styles.hintText}>
                    Static key-value pairs merged into every call's parameters block. Use for custom fields like practice names, callback numbers, etc.
                </p>

                {formState.extra_parameters.length === 0 && (
                    <div className={styles.emptyParams}>No extra parameters added</div>
                )}

                {formState.extra_parameters.map((param, i) => (
                    <div key={i} className={styles.paramRow}>
                        <input
                            type="text"
                            className={styles.input}
                            placeholder="Key"
                            value={param.key}
                            onChange={e => updateParam(i, 'key', e.target.value)}
                        />
                        <input
                            type="text"
                            className={styles.input}
                            placeholder="Value"
                            value={param.value}
                            onChange={e => updateParam(i, 'value', e.target.value)}
                        />
                        <button
                            type="button"
                            className={styles.removeParamBtn}
                            onClick={() => removeParam(i)}
                            title="Remove parameter"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>

            {/* ── Payload preview ────────────────────────────────────────── */}
            <div className={styles.formSection}>
                <div className={styles.formSectionTitle}>Payload Preview</div>
                <p className={styles.hintText}>
                    This is what the frontend sends. The backend injects webhook_url, api_key, llm_provider, tts_provider, and phone_country_code automatically.
                </p>
                <pre className={styles.payloadPreview}>
                    {JSON.stringify(
                        {
                            calling_system_call_type: formState.calling_system_call_type || CALL_TYPE_LABELS[callType],
                            calling_system_entity_type: formState.calling_system_entity_type || '<entity_type>',
                            schedule_type: formState.schedule_type,
                            ...(formState.schedule_type === 'one_time'
                                ? { scheduled_at: formState.scheduled_at || '<datetime>' }
                                : { cron_expression: formState.cron_expression || '<cron>' }),
                            ...(formState.extra_parameters.length > 0 && {
                                extra_parameters: Object.fromEntries(
                                    formState.extra_parameters
                                        .filter(p => p.key)
                                        .map(p => [p.key, p.value])
                                ),
                            }),
                            service_log_ids: [`...${selectedCount} id(s)`],
                        },
                        null, 2,
                    )}
                </pre>
            </div>
        </div>
    );
};

export default Step3ScheduleConfig;